import { useInView } from '../../../hooks/useInView';

interface FacilityItem {
  icon: string;
  title: string;
  body: string;
}

const equipmentItems: FacilityItem[] = [
  {
    icon: 'ri-pulse-line',
    title: '脱毛機器',
    body: '蓄熱式光脱毛機（IPL方式）を導入しています。メラニンに反応するのではなく、毛包組織を穏やかに加温するタイプで、日焼け肌や色黒の方でもご利用いただけます。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '衛生管理',
    body: '施術ごとにタオル・ガウンを新品と交換し、器具はオートクレーブ（高圧蒸気滅菌器）にて滅菌処理を行っています。使い捨て器材を優先的に採用しています。',
  },
];

const productItems: FacilityItem[] = [
  {
    icon: 'ri-flask-line',
    title: '使用化粧品',
    body: '敏感肌・アレルギー肌の方にも対応できる、パラベンフリー・無香料の国産低刺激コスメを採用しています。全成分は施術前にご確認いただけます。',
  },
  {
    icon: 'ri-eye-close-line',
    title: 'まつげ専用グルー',
    body: 'まつげエクステには、ホルムアルデヒドフリーのプレミアムグルーを使用しています。持続性・柔軟性・低刺激性のバランスを重視し、日本製の認定製品に限定しています。',
  },
];

function FacilityCard({ item, delay }: { item: FacilityItem; delay: number }) {
  const { ref, isInView } = useInView<HTMLDivElement>({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className="flex items-start gap-5 transition-all duration-700"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateX(0)' : 'translateX(-24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      <div
        className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-gold/50 mt-0.5"
        style={{ borderRadius: '2px' }}
      >
        <i className={`${item.icon} text-gold text-lg`} />
      </div>
      <div>
        <h4 className="font-serif-jp text-offwhite font-medium mb-2" style={{ fontSize: 'clamp(15px, 2vw, 17px)' }}>
          {item.title}
        </h4>
        <p className="font-sans-jp leading-relaxed" style={{ fontSize: 'clamp(12px, 1.6vw, 14px)', color: '#B8B8B8', lineHeight: '1.9' }}>
          {item.body}
        </p>
      </div>
    </div>
  );
}

export default function Facilities() {
  const { ref: titleRef, isInView: titleInView } = useInView<HTMLDivElement>();

  return (
    <section id="facilities" className="py-20 sm:py-28 lg:py-36 bg-charcoal">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        {/* Section title */}
        <div
          ref={titleRef}
          className="mb-12 sm:mb-16 transition-all duration-700"
          style={{
            opacity: titleInView ? 1 : 0,
            transform: titleInView ? 'translateY(0)' : 'translateY(32px)',
          }}
        >
          <p className="section-label mb-4" style={{ color: '#C9A96E' }}>FACILITIES &amp; SAFETY</p>
          <h2 className="font-serif-jp text-offwhite leading-snug" style={{ fontSize: 'clamp(28px, 5vw, 48px)' }}>
            設備と安全への
            <br />
            取り組み
          </h2>
        </div>

        {/* Image + content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left image */}
          <div
            className="relative overflow-hidden"
            style={{ borderRadius: '4px', aspectRatio: '4/3' }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=professional%20esthetic%20salon%20interior%20with%20sterile%20clean%20medical%20equipment%20on%20white%20tray%2C%20high-end%20beauty%20tools%2C%20sterilized%20surgical%20instruments%20arranged%20neatly%2C%20autoclave%20machine%20in%20background%2C%20premium%20clinical%20beauty%20salon%20setting%20with%20soft%20lighting%20on%20cream%20walls&width=700&height=520&seq=facility001&orientation=landscape"
              alt="サロン設備"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 to-transparent" />
          </div>

          {/* Right content */}
          <div>
            {/* Equipment */}
            <div className="mb-10">
              <p className="font-sans-jp text-gold mb-6" style={{ fontSize: '11px', letterSpacing: '0.25em' }}>
                EQUIPMENT
              </p>
              <div className="space-y-8">
                {equipmentItems.map((item, idx) => (
                  <FacilityCard key={item.title} item={item} delay={idx * 120} />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-white/10 my-8" />

            {/* Products */}
            <div>
              <p className="font-sans-jp text-gold mb-6" style={{ fontSize: '11px', letterSpacing: '0.25em' }}>
                PRODUCTS
              </p>
              <div className="space-y-8">
                {productItems.map((item, idx) => (
                  <FacilityCard key={item.title} item={item} delay={200 + idx * 120} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
